import PortfolioCategory from '@business/portfolio/PortfolioCategory';
import PortfolioItem from '@business/portfolio/PortfolioItem';
import { useBuildMetaTitle, useSetGlobalAppLanguage } from '@components/Hooks';
import Layout from '@components/layout/Layout';
import PortfolioCategoryView from '@components/portfolio/PortfolioCategoryView';
import config from '@config';
import AppSetting from '@xFrame4/business/AppSetting';
import CustomHead from '@xFrame4/components/next/CustomHead';
import { GetStaticPaths, GetStaticProps } from 'next';
import { getCommonStaticProps } from 'pages';
import { FunctionComponent } from 'react';
import { PageWithLanguageParams, PageWithLanguageProps } from '..';

interface PortfolioCategoryPageParams extends PageWithLanguageParams
{
    slug: string;
}

interface PortfolioCategoryPageProps extends PageWithLanguageProps
{
    portfolioCategory: PortfolioCategory;
    portfolioItems: PortfolioItem[];
}

export const getStaticPaths: GetStaticPaths<PortfolioCategoryPageParams> = async () =>
{
    let paths: Array<{ params: PortfolioCategoryPageParams }> = [];
    
    for (let language of config.languages)
    {
        let portfolioCategoryResult = await PortfolioCategory.manager.load({ translation_Language: language.code });
        if (portfolioCategoryResult != null)
        {
            for (let portfolioCategory of portfolioCategoryResult.entities)
            {
                let translation = portfolioCategory.getTranslation(language.code);
                if (translation !== undefined)
                {
                    let path: { params: PortfolioCategoryPageParams } = {
                        params: {
                            language: language.code,
                            slug: translation.slug
                        }
                    }
                    paths.push(path);
                }
            }
        }
    }

    return {
        paths,
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps<PortfolioCategoryPageProps, PortfolioCategoryPageParams> = async (context) => 
{
    // Common page props
    let commonPageProps = await getCommonStaticProps();

    // Portfolio category
    let portfolioCategory = await PortfolioCategory.manager.get({ translation_Language: context.params?.language, translation_Slug: context.params?.slug });

    // Portfolio items
    let portfolioItems: PortfolioItem[] = [];
    let portfolioItemResult = await PortfolioItem.manager.load({ category_Id: portfolioCategory?.id, translation_Language: context.params?.language });
    if (portfolioItemResult != null) portfolioItems = portfolioItemResult.entities;

    return {
        props: {
           ...commonPageProps,
           language: config.languages.find(l => l.code == context.params?.language)!,
           portfolioCategory: portfolioCategory != null ? portfolioCategory.toJson() : null,
           portfolioItems: PortfolioItem.arrayToJson(portfolioItems)
        }
    };
}

const PortfolioCategoryPage: FunctionComponent<PortfolioCategoryPageProps> = (props) =>
{
    useSetGlobalAppLanguage(props.language);

    let portfolioCategory = PortfolioCategory.createFromJson(props.portfolioCategory) as PortfolioCategory;
    let translation = portfolioCategory.getTranslation(props.language.code);
    let metaTitle = useBuildMetaTitle(translation?.seoTitle != '' ? translation?.seoTitle : translation?.title);
    
    /** Render */
    return (
        <>
            <CustomHead 
                metaData={{
                    title: metaTitle,
                    metaDescription: translation?.seoDescription,
                    metaKeywords: translation?.seoKeywords
                }}
            />

            <Layout 
                menuItems={props.menuItems}
                appSettings={AppSetting.createArrayFromJson(props.appSettings) as AppSetting[]}
            >
                <PortfolioCategoryView 
                    portfolioCategory={portfolioCategory}
                    portfolioItems={PortfolioItem.createArrayFromJson(props.portfolioItems) as PortfolioItem[]}
                />
            </Layout>
        </>
    );
}

export default PortfolioCategoryPage;